// iOS-style interactive swipe-back, web-only. The Flutter WebView does NOT enable
// WKWebView's native back-gesture (allowsBackForwardNavigationGestures is off) and
// intercepts nothing, so this is the only "swipe to go back". It drives react-router
// history inside the current tab's WebView — never across the native bottom-nav tabs.
//
// Safe by construction: only starts from the very left edge, only when there IS an
// in-app page to go back to (never backs out of a tab's entry page), yields to
// vertical scrolling and horizontal carousels/inputs/modals, is disabled on
// payment/auth/staff/TV routes, and honours prefers-reduced-motion (instant nav,
// no transform).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

const EDGE = 28;        // px from the left edge where the gesture may start
const CONFIRM = 10;     // px of horizontal travel before we claim the gesture
const COMMIT = 72;      // px past which releasing triggers "back"

const NO_SWIPE_SELECTOR = [
  'input', 'textarea', 'select', '[contenteditable="true"]',
  '[role="dialog"]', '.modal', '[data-no-swipeback]',
  // any open modal/bottom-sheet overlay (loyalty/contact/rhythm/vip/subscription-sheet…)
  '[class*="-overlay"]',
  // horizontal scrollers/carousels that live inside the left-edge band
  '.club-stories-track', '.club-carousel-track', '.club-carousel',
  '[class*="-related-row"]', '.services-tabs', '[class*="-scroll"]',
].join(',');

// Never hijack back on these flows (payment/booking/auth) or non-client surfaces.
const BLOCKED_PREFIXES = [
  '/booking-flow', '/booking-payment', '/subscription-payment',
  '/search', '/status-', '/pilling-', '/table', '/widgets', '/staff',
  '/reception', '/massage-status',
];

function reducedMotion() {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export default function SwipeBack({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const wrapRef = useRef(null);
  const depthRef = useRef(0);      // in-app history depth since load (0 = entry page)
  const gestureRef = useRef(null);
  const [backdrop, setBackdrop] = useState(0);

  useEffect(() => {
    if (navType === 'PUSH') depthRef.current += 1;
    else if (navType === 'POP' && depthRef.current > 0) depthRef.current -= 1;
  }, [location, navType]);

  const reset = useCallback((animate) => {
    const el = wrapRef.current;
    if (el) {
      el.style.transition = animate ? 'transform 0.22s ease' : 'none';
      el.style.transform = 'translateX(0)';
    }
    setBackdrop(0);
    gestureRef.current = null;
  }, []);

  // whenever the route actually changes, drop any leftover drag transform
  useEffect(() => { reset(false); }, [location.pathname, reset]);

  useEffect(() => {
    const blocked = BLOCKED_PREFIXES.some((p) => location.pathname.startsWith(p));
    if (blocked) return undefined;

    function onStart(e) {
      if (e.touches.length !== 1 || depthRef.current <= 0) return;
      const t = e.touches[0];
      if (t.clientX > EDGE) return;
      if (e.target.closest && e.target.closest(NO_SWIPE_SELECTOR)) return;
      gestureRef.current = { x0: t.clientX, y0: t.clientY, confirmed: false, dx: 0 };
    }

    function onMove(e) {
      const g = gestureRef.current;
      if (!g) return;
      const t = e.touches[0];
      const dx = t.clientX - g.x0;
      const dy = t.clientY - g.y0;
      if (!g.confirmed) {
        if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { gestureRef.current = null; return; }
        if (dx > CONFIRM) g.confirmed = true; else return;
      }
      g.dx = Math.max(0, dx);
      if (e.cancelable) e.preventDefault();   // we own this gesture; stop scroll
      if (!reducedMotion()) {
        const el = wrapRef.current;
        if (el) { el.style.transition = 'none'; el.style.transform = `translateX(${g.dx}px)`; }
        setBackdrop(Math.min(0.18, g.dx / 1600));
      }
    }

    function onEnd() {
      const g = gestureRef.current;
      if (!g || !g.confirmed) { gestureRef.current = null; return; }
      if (g.dx > COMMIT) {
        if (reducedMotion()) { gestureRef.current = null; navigate(-1); return; }
        const el = wrapRef.current;
        if (el) {
          el.style.transition = 'transform 0.18s ease';
          el.style.transform = `translateX(${Math.max(g.dx, window.innerWidth * 0.55)}px)`;
        }
        gestureRef.current = null;
        setTimeout(() => navigate(-1), 140);   // route change → reset() clears transform
      } else {
        reset(true);
      }
    }

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [location.pathname, navigate, reset]);

  return (
    <>
      <div
        aria-hidden="true"
        style={{
          position: 'fixed', inset: 0, background: '#000', opacity: backdrop,
          pointerEvents: 'none', zIndex: 0, transition: 'opacity 0.15s ease',
        }}
      />
      <div ref={wrapRef} style={{ position: 'relative', zIndex: 1, willChange: 'transform' }}>
        {children}
      </div>
    </>
  );
}
